import {BrowsePageContent} from '@udemy/browse-aggregator';
import {BrowseCourse} from '@udemy/browse-course';
import {GwgAccelerator} from '@udemy/career-academies';
import {FeatureVariantAssignment} from '@udemy/graphql';
import {HeadSeoTypes} from '@udemy/head-seo';
import {WithI18nProps} from '@udemy/i18n';
import {
    type CertificationBundle,
    type CertificationModel,
} from '@udemy/react-certification-preparation';
import type {RelatedCategoryItem} from '@udemy/react-recommendations';
import {
    SubscriptionPlanContext,
    SubscriptionPlanSubscriberState,
    ConsumerSubscriptionGwGStandalonePlan,
    ConsumerSubscriptionGwGPersonalPlan,
} from '@udemy/subscription-graphql';
import {WithUDDataProps} from '@udemy/ud-data';

import {SchemaMarkup} from 'src/components/schema-markup/types';
import {Topic as TopicModel} from 'src/generated/graphql';

import {TOPIC_PRESET_VALUES} from '../constants';

const emptyStr = '';
export type TopicPresetValue =
    | (typeof TOPIC_PRESET_VALUES)[keyof typeof TOPIC_PRESET_VALUES]
    | typeof emptyStr;

interface TrackingContext {
    tracking_id?: string;
    frontendTrackingId?: string;
}

/** Represents the topic information */
export type Topic = TopicModel & TrackingContext;

/** Represents a review of a course */
export type CourseReview = {
    course_id: number;
    course: Pick<BrowseCourse, '_class' | 'id' | 'title' | 'url'>;
    course_review_id: number;
    locale_id: string;
    text: string;
};

/** Represents a related page link */
export type RelatedPage = {
    title: string;
    url: string;
    type: string;
    content_length?: number;
    lecture_id: number;
    primary_topic_title?: string;
    source: {
        title: string;
        url: string;
    };
    kwargs: {
        url_title?: string;
        preset_filter_val?: string;
    };
} & TrackingContext;

/** Represents a question and its answer */
export type QuestionAndAnswer = {
    question: string;
    answer: string;
    link_url: string;
    link_text: string;
    locale_id: string;
    page_type: string;
};

/** Represents redirect context of the topic data */
export type RedirectContext = {
    destination: string;
    permanent: boolean;
};

export type TopicContext = {
    topicTitle: string;
    highlightedCourseReview?: CourseReview;
    primaryDescription: string;
    secondaryDescription: string;
    metaProps: {
        seoDescription: string;
        seoTitle: string;
        seoRobotRules?: HeadSeoTypes['robots'];
    };
    enrollmentStat: string;
    /**
     * Raw enrollment count (integer) from the monolith topic context. Unlike `enrollmentStat`, it is
     * not localized and has no threshold applied by the backend, so the frontend handles both. It is
     * only present when the topic has a `tertiaryDescription` or `certDescription`.
     */
    numEnrollments?: number;
    relatedPages: RelatedPage[];
    presetValue: string;
    questionsAndAnswers: QuestionAndAnswer[];
    schema_markup: SchemaMarkup;
    topic: Topic;
    redirect_context?: RedirectContext;
    tertiaryDescription?: string;
    numCourses?: number;
    avgRating?: number;
    numExercises?: number;
    certDescription?: string;
    relatedCertificationBrowseAggregators: BrowsePageContent[];
    relatedCategoriesAndSubcategories?: RelatedCategoryItem[];
};

export enum HEADER_VARIANT {
    /** Skeleton placeholder shown while topic-page experiment configs are still resolving. */
    LOADING = 'loading',
    /** Legacy certification banner. */
    CERTIFICATION = 'certification',
    CONTEXTUAL = 'contextual',
    DEFAULT = 'default',
}

export type HeaderVariant =
    | {type: HEADER_VARIANT.LOADING}
    | {
          type: HEADER_VARIANT.CERTIFICATION;
          certification: CertificationModel;
          relatedCertificationBrowseAggregators: TopicContext['relatedCertificationBrowseAggregators'];
          browseAggregatorPageEnabled?: boolean;
      }
    | {
          type: HEADER_VARIANT.CONTEXTUAL;
          description: string;
          numCourses?: number;
          numExercises?: number;
          avgRating?: number;
      }
    | {
          type: HEADER_VARIANT.DEFAULT;
          topicName: string;
      };

export enum BUNDLE_VARIANT {
    CERTIFICATION = 'certification',
    GENERIC = 'generic',
    VOUCHER_ONLY = 'voucher_only',
}

export type BundleVariant =
    | {
          type: BUNDLE_VARIANT.CERTIFICATION;
          certificationBundles: CertificationBundle[];
          showCertificationVoucherBundleUnit?: boolean;
      }
    | {
          type: BUNDLE_VARIANT.GENERIC;
          courses: BrowseCourse[];
          topicName: string;
          enableSpecialBundlePricing: boolean;
      }
    | {type: BUNDLE_VARIANT.VOUCHER_ONLY};

export interface TopicPageExperimentVariants {
    /** TopicWrapper: mobile vertical / mweb standardized product-card layout for browse course cards */
    useVerticalRecoCourseCardStyle?: boolean;
    /**
     * True when mweb standardized mobile cards OR MX mobile vertical reco (Tapen) is on.
     * Mirrors category `threeCardVerticalLayoutExperimentIsEnabled` for discovery unit props / ContentArea.
     */
    threeCardVerticalLayoutExperimentIsEnabled?: boolean;
    isExperimentConfigsLoading?: boolean;
    showCodingExercisesBadge?: boolean;
    showUpdatedPPTopicPage?: boolean;
    showPersonalPlanBadge?: boolean;
    showCertificationBanner?: boolean;
    browseAggregatorPageEnabled?: boolean;
    showCourseGuidance?: boolean;
    fullQuickViewBoxFeature?: boolean;
    topicCatSubcatMwebCardStandardizationFeature?: FeatureVariantAssignment;
    showTopicSpecialBundlePricing?: FeatureVariantAssignment;
    isTopicVoucherModuleAboveBundle?: FeatureVariantAssignment;
    certificationVoucherBundleUnitFeature?: FeatureVariantAssignment;
}

export interface TopicProps
    extends TopicContext,
        TopicPageExperimentVariants,
        WithUDDataProps,
        WithI18nProps {
    subsCollectionIds?: string;
    isConsumerSubsSubscriber: boolean;
    isSubsLoading?: boolean;
    // In the server side, withMatchMedia is returning null
    isMobile?: boolean | null;
    certification?: CertificationModel;
    certificationBundles: CertificationBundle[];
    isCertificationTopic?: boolean;
    genericTopicBundleCourses?: BrowseCourse[];
    enableLabsInPersonalPlan?: boolean;
    enableAssessmentsInPersonalPlan?: boolean;
    isLowSupplyTopicPage?: boolean;
    activeSubscriptionPlan?: SubscriptionPlanSubscriberState;
    gwgSubscriptionPlan?: SubscriptionPlanContext<ConsumerSubscriptionGwGStandalonePlan>;
    gwgPlusPPSubscriptionPlan?: SubscriptionPlanContext<ConsumerSubscriptionGwGPersonalPlan>;
    showGwgCarousel?: boolean;
    gwgCarouselAccelerator?: GwgAccelerator;
}
